import { DynamicForm } from "@shared/components/DynamicForm";
import { GenericTable } from "@shared/components/GenericTable";
import { FormField, FormService } from "@shared/services/FormServices";
import { Button } from "@shared/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@shared/ui/tooltip";
import { PAGE_SIZE } from "@shared/utils/constants";
import { Download, Eye } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAreaList, useExportConsumerList, useSubAreaList } from "../hooks";

interface Consumer {
  id: string;
  name: string;
  accountNumber: string;
  premiseId: string;
  area: string;
  subarea: string;
  lastBilledDate: string;
  billedAmount: string;
  status: "ACTIVE" | "PAUSE" | "DISCONNECTED";
  lastBillType: "Actual" | "Estimated";
}

interface ConsumerlistProps {
  selectedConsumers: string[];
  onSelectionChange: (selectedIds: string[]) => void;
  consumersList: any;
  action?: string;
  onSetFilters: (params: any) => void;
  queryParams?: any;
}

export const BillCycleConsumerList: React.FC<ConsumerlistProps> = ({
  selectedConsumers,
  onSelectionChange,
  consumersList,
  action,
  onSetFilters,
  queryParams,
}) => {
  const params = useParams();
  const id = params?.id;
  const [isExport, setIsExport] = useState(false);
  const [consumerQueryParams, setConsumerQueryParams] = useState({
    page: 1,
    status: "",
    area: [],
    sub_area: [],
    export: false,
  });
  const { data: consumerList } = useExportConsumerList(
    consumerQueryParams,
    id,
    isExport
  );
  const [subAreaListConfig, setSubArea] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const { data: areaList } = useAreaList();
  const navigate = useNavigate();
  const filterFields: FormField[] = [
    {
      name: "status",
      label: "Status",
      type: "select",
      placeholder: "All Status",
      required: false,
      options: [
        { value: "Active", label: "Active" },
        { value: "Inactive", label: "Inactive" },
      ],
      classes: {
        container: "w-full mb-6",
        // label: "block text-sm font-medium text-gray-700 mb-2",
        // input: "w-full h-12 px-4 border border-gray-300 rounded-lg",
        // error: "text-red-600 text-sm mt-1",
        // select:
        //   "w-full h-12 px-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500",
        // helperText: "text-xs text-gray-500 mt-1",
      },
    },
    {
      name: "area",
      label: "Area",
      type: "multi-select",
      placeholder: "Select Area",
      required: false,
      options: areaList?.map((area: any) => ({
        value: area.id,
        label: area.name,
      })),
      className: "col-span-1",
      classes: {
        container: "w-full mb-6",
        // label: "block text-sm font-medium text-gray-700 mb-2",
        // input: "w-full h-12 px-4 border border-gray-300 rounded-lg",
        // error: "text-red-600 text-sm mt-1",
        // select:
        //   "w-full h-12 px-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500",
        // helperText: "text-xs text-gray-500 mt-1",
      },
    },

    {
      name: "sub_area",
      label: "Sub Area",
      type: "multi-select",
      placeholder: "Select Sub Area",
      required: false,
      options: subAreaListConfig,
      className: "col-span-1",
      classes: {
        // label: "block text-sm font-medium text-gray-700 mb-2",
        // input: "w-full h-12 px-4 border border-gray-300 rounded-lg",
        // error: "text-red-600 text-sm mt-1",
        // select:
        //   "w-full h-12 px-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500",
        // helperText: "text-xs text-gray-500 mt-1",
      },
    },
  ];

  console.log(
    "consumersList as per premise:",
    JSON.stringify(consumersList, null, 2)
  );
  const form = FormService.createForm({
    fields: filterFields,
    mode: "onChange",
    validateOnMount: false,
  });

  console.log("query params are:", queryParams);

  const watchedValues = form.watch();

  const { data: subAreaList = [] } = useSubAreaList(
    watchedValues.area && Array.isArray(watchedValues.area)
      ? watchedValues.area
      : []
  );

  useEffect(() => {
    setSubArea(
      subAreaList.map((subCategory: any) => ({
        value: subCategory.id,
        label: subCategory.name,
      }))
    );
  }, [subAreaList]);

  const getStatusBadgeClass = (status: Consumer["status"]) => {
    switch (status) {
      case "ACTIVE":
        return "bg-green-100 text-green-800";
      case "PAUSE":
        return "bg-amber-100 text-amber-800";
      case "DISCONNECTED":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-blue-100 text-gray-800";
    }
  };

  // Define columns for the GenericTable
  const getColumns = (action: string) => {
    const baseColumns = [
      {
        key: "consumerInfo",
        header: "Consumer",
        render: (row) => (
          <div>
            <div className="font-medium">{row.consumerName}</div>
            <div className="text-sm text-gray-500">{row.accountNo}</div>
          </div>
        ),
      },
      { key: "premise", header: "Premise" },
      {
        key: "address",
        header: "Area/Subarea",
        render: (row) => (
          <div>
            <div>{row.area ?? "NA"}</div>
            <div className="text-sm text-gray-500">{row.subArea ?? "NA"}</div>
          </div>
        ),
      },
      {
        key: "consumerStatus",
        header: "Status",
        render: (value) => (
          <span
            className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeClass(
              value
            )}`}
          >
            {value ?? "NA"}
          </span>
        ),
      },
      {
        key: "actions",
        header: "Actions",
        render: (_, row) => (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0"
                  onClick={() => handleView(row)}
                >
                  <Eye size={16} />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>View Consumer Profile</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ),
      },
    ];

    // Conditionally add billing-related columns if in "view" mode
    if (action == "view") {
      baseColumns.splice(
        3,
        0,
        { key: "createdDate", header: "Last Billed Date" },
        {
          key: "totalChargeExclusiveOfTax",
          header: "Billed Amount",
          render: (value) => <div>{value}</div>,
        },
        {
          key: "billStateLabel",
          header: "Last Bill Type",
          render: (value) => (
            <span
              className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeClass(
                value
              )}`}
            >
              {value ?? "NA"}
            </span>
          ),
        }
      );
    }

    return baseColumns;
  };

  const handleExport = () => {
    setConsumerQueryParams({
      ...consumerQueryParams,
      export: true,
    });
    setIsExport(true);
  };

  const handleView = (row: any) => {
    navigate(`/cx/consumer-management/${row.remoteConsumerNumber}`);
  };

  // Bulk actions for selected rows
  const bulkActions = [
    {
      label: "Export Selected",
      icon: <Download size={16} />,
      onClick: (selectedData: Consumer[]) => {
        console.log("Exporting selected data:", selectedData);
        // Implement actual export logic here
      },
    },
  ];

  const handleFilterSubmit = () => {
    const formValues = form.getValues();
    onSetFilters({
      status: formValues.status || "",
      area: formValues.area || [],
      sub_area: formValues.sub_area || [],
      page: 1, // Reset to first page when filtering
    });
    setConsumerQueryParams({
      area: formValues.area || [],
      page: 1,
      sub_area: formValues.sub_area,
      status: formValues.status || "",
      export: false,
    });
  };

  useEffect(() => {
    if (isExport) {
      // ✅ Wait for API call, then reset
      const timer = setTimeout(() => setIsExport(false), 1000);
      return () => clearTimeout(timer);
    }
  }, [isExport]);

  const handleResetFilters = () => {
    form.reset();

    // Send reset to parent
    onSetFilters({
      status: "",
      area: [],
      sub_area: [],
      page: 1,
    });
  };

  const filters = (
    <div>
      <DynamicForm
        fields={filterFields}
        form={form}
        onReset={handleResetFilters}
        gridColumns={3}
        config={{
          layout: "vertical",
          showSubmitButton: false,
          showResetButton: false,
          classes: {
            form: "gap-4",
          },
        }}
        onSubmit={function (data: any): void | Promise<void> {
          throw new Error("Function not implemented.");
        }}
      />
      <div className="flex gap-3">
        <Button type="button" variant="outline" onClick={handleResetFilters}>
          Reset
        </Button>

        <Button type="button" onClick={handleFilterSubmit}>
          Apply Filters
        </Button>
      </div>
    </div>
  );
  const handleSearchChange = (value: string) => {
    onSetFilters({
      ...queryParams,
      search_data: value.length > 2 ? value : "",
      page: 1,
    });
  };

  const handlePageChange = (newPage: number) => {
    onSetFilters({
      ...queryParams,
      page: newPage,
    });
    setCurrentPage(newPage);
  };

  const handleSelectionChange = (newSelectedRows: any) => {
    onSelectionChange(newSelectedRows);
  };

  const isSelectable = params?.action == "edit" ? true : false;

  return (
    <div className="">
      <GenericTable
        data={consumersList ?? []}
        columns={getColumns(action)}
        rowKey="id"
        // selectable={isSelectable}
        selectedRows={selectedConsumers}
        bulkActions={bulkActions}
        filterPanel={filters}
        onExport={handleExport}
        searchPlaceholder="Search by name or account..."
        onSearch={handleSearchChange}
        emptyMessage="No consumers available"
        onPageChange={handlePageChange}
        pagination={{
          currentPage: currentPage,
          totalPages: Math.ceil((consumersList?.length ?? 0) / PAGE_SIZE),
          totalItems: consumersList?.length ?? 0,
          itemsPerPage: PAGE_SIZE,
          startItem: (queryParams.page - 1) * PAGE_SIZE + 1,
          endItem: Math.min(
            queryParams.page * PAGE_SIZE,
            consumersList?.length ?? 0
          ),
        }}
      />
    </div>
  );
};
